// Deterministic replays for Lesson 4.11 ("An Agent That Passes Once Is Not Reliable"). The
// trials, verdicts, traces and the failure table below were recorded from the Workshop 4.2
// measurement of 8 October 2026 with zai.glm-5: twenty live-task questions, five trials each.
// The fourth trace is the every-piece run from Lesson 4.5's alignment data. The pass@k graphic
// computes from the formula. Nothing here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export type Verdict = 'pass' | 'fail';

// --- agenteval-trials: one task, five trials ------------------------------------------------

export const TRIAL_QUESTIONS = ['a1', 'a2', 'a7', 'a13'] as const;
export type TrialQuestion = (typeof TRIAL_QUESTIONS)[number];
export const trialQuestionLabels: Record<TrialQuestion, string> = {
  a1: 'What is blocking the launch?',
  a2: 'Who owns the dates task, and is it done?',
  a7: 'Is the room booked?',
  a13: 'Has the headcount been sent to the venue?',
};
export const toTrialQuestion = (value: string): TrialQuestion =>
  TRIAL_QUESTIONS.find((id) => id === value) ?? 'a1';
export interface Trial {
  trial: number;
  /** The lookups the trial made, in order. */
  tools: readonly string[];
  /** The first words of the recorded answer. */
  answer: string;
  /** What the grader on the words said. */
  words: Verdict;
}
const trial = (n: number, tools: readonly string[], answer: string, words: Verdict): Trial => ({
  trial: n,
  tools,
  answer,
  words,
});
export const BLOCKING_TOOLS = ['list_projects', 'list_tasks'] as const;
export const DATES_TOOLS = ['list_projects', 'list_tasks(1)', 'list_tasks(2)'] as const;
export const BLOCKING_ANSWER = 'names the projector, the headcount and the dates';
export const DATES_ANSWER = 'Check the dates (id 10) in Apollo is not done and has no assignee';
export const DATES_REFUSAL =
  "I need more information. There are multiple tasks with 'dates' in the title across projects. Which project?";
export const ROOM_REFUSAL = "I don't have access to room booking information …";
export const HEADCOUNT_REFUSAL =
  "I don't have access to information about headcount being sent to venues …";
export const trialRuns: Record<TrialQuestion, readonly Trial[]> = {
  a1: [1, 2, 3, 4, 5].map((n) => trial(n, BLOCKING_TOOLS, BLOCKING_ANSWER, 'pass')),
  a2: [
    trial(1, DATES_TOOLS, DATES_ANSWER, 'pass'),
    trial(2, [], DATES_REFUSAL, 'fail'),
    trial(3, DATES_TOOLS, DATES_ANSWER, 'pass'),
    trial(4, DATES_TOOLS, DATES_ANSWER, 'pass'),
    trial(5, DATES_TOOLS, DATES_ANSWER, 'pass'),
  ],
  a7: [
    trial(1, [], ROOM_REFUSAL, 'fail'),
    trial(2, [], ROOM_REFUSAL, 'fail'),
    trial(3, [], ROOM_REFUSAL, 'fail'),
    trial(4, [], ROOM_REFUSAL, 'pass'),
    trial(5, [], ROOM_REFUSAL, 'pass'),
  ],
  a13: [
    trial(1, [], HEADCOUNT_REFUSAL, 'fail'),
    trial(2, [], HEADCOUNT_REFUSAL, 'pass'),
    trial(3, [], HEADCOUNT_REFUSAL, 'fail'),
    trial(4, [], HEADCOUNT_REFUSAL, 'fail'),
    trial(5, [], HEADCOUNT_REFUSAL, 'fail'),
  ],
};
/** The verdict on the words alone, or with the data assertion "a lookup happened" in front. */
export const verdict = (entry: Trial, dataFirst: boolean): Verdict =>
  dataFirst && entry.tools.length === 0 ? 'fail' : entry.words;
export const passedCount = (question: TrialQuestion, dataFirst: boolean) =>
  trialRuns[question].filter((entry) => verdict(entry, dataFirst) === 'pass').length;
export const yesNo = (value: boolean) => (value ? 'yes' : 'no');
export const passAtFive = (question: TrialQuestion, dataFirst: boolean) =>
  yesNo(passedCount(question, dataFirst) > 0);
export const passPowFive = (question: TrialQuestion, dataFirst: boolean) =>
  yesNo(passedCount(question, dataFirst) === trialRuns[question].length);
export interface TrialsState {
  question: TrialQuestion;
  dataFirst: boolean;
  run: boolean;
}
export const initialTrials = (): TrialsState => ({ question: 'a1', dataFirst: false, run: false });
export type TrialsAction =
  | { type: 'question'; value: TrialQuestion }
  | { type: 'toggle-data-first' }
  | { type: 'run' }
  | { type: 'reset' };
export function trialsTransition(state: TrialsState, action: TrialsAction): TrialsState {
  switch (action.type) {
    case 'reset':
      return initialTrials();
    case 'question':
      return { ...state, question: action.value, run: false };
    case 'toggle-data-first':
      return { ...state, dataFirst: !state.dataFirst };
    case 'run':
      return { ...state, run: true };
  }
}
export const TRIALS_START_MESSAGE = 'Choose a question, then press Run five trials.';
export const BLOCKING_MESSAGE = 'All five trials listed the tasks and named the three blockers.';
export const DATES_MESSAGE = 'One trial asked which project was meant instead of listing anything.';
export const ROOM_WORDS_MESSAGE = 'Two passes were refusals that happened to contain "booked".';
export const HEADCOUNT_WORDS_MESSAGE = 'The one pass found "no" inside "another" in a refusal.';
export const NO_LOOKUP_MESSAGE = 'No trial did a lookup, so the honest count is zero of five.';
export const TRIALS_REFINEMENT =
  'Refinement: a7\'s two passes were refusals that contained "booked", and grading the data first removes them.';
export function trialsSummary(state: TrialsState): Summary {
  if (!state.run) return { tone: 'neutral', message: TRIALS_START_MESSAGE };
  switch (state.question) {
    case 'a1':
      return { tone: 'success', message: BLOCKING_MESSAGE };
    case 'a2':
      return { tone: 'neutral', message: DATES_MESSAGE };
    case 'a7':
      return state.dataFirst
        ? { tone: 'success', message: NO_LOOKUP_MESSAGE }
        : { tone: 'warning', message: ROOM_WORDS_MESSAGE };
    case 'a13':
      return state.dataFirst
        ? { tone: 'success', message: NO_LOOKUP_MESSAGE }
        : { tone: 'warning', message: HEADCOUNT_WORDS_MESSAGE };
  }
}

// --- agenteval-passk: pass@k and pass^k from one chance per trial -------------------------

export const CHANCES = [0.4, 0.7, 0.9, 0.99] as const;
export type Chance = (typeof CHANCES)[number];
export const toChance = (value: string): Chance =>
  CHANCES.find((chance) => String(chance) === value) ?? 0.4;
export const KS = [1, 3, 5, 10] as const;
export type K = (typeof KS)[number];
export const toK = (value: string): K => KS.find((k) => String(k) === value) ?? 1;
export const passAtK = (p: number, k: number) => 1 - (1 - p) ** k;
export const passPowK = (p: number, k: number) => p ** k;
/** Whole percent, with "about" at the ends where rounding would hide the remainder. */
export const percentText = (value: number) => {
  if (value >= 0.995) return 'about 100%';
  if (value > 0 && value < 0.005) return 'about 0%';
  return `${Math.round(value * 100)}%`;
};
export const gapPoints = (p: number, k: number) =>
  Math.round((passAtK(p, k) - passPowK(p, k)) * 100);
export const gapText = (p: number, k: number) => {
  const points = gapPoints(p, k);
  return points === 1 ? '1 point' : `${points} points`;
};
export interface PassKState {
  chance: Chance;
  k: K;
  computed: boolean;
}
export const initialPassK = (): PassKState => ({ chance: 0.4, k: 1, computed: false });
export type PassKAction =
  | { type: 'chance'; value: Chance }
  | { type: 'k'; value: K }
  | { type: 'compute' }
  | { type: 'reset' };
export function passKTransition(state: PassKState, action: PassKAction): PassKState {
  switch (action.type) {
    case 'reset':
      return initialPassK();
    case 'chance':
      return { ...state, chance: action.value, computed: false };
    case 'k':
      return { ...state, k: action.value, computed: false };
    case 'compute':
      return { ...state, computed: true };
  }
}
export const PASSK_START_MESSAGE = 'Choose a chance per trial and a k, then press Compute.';
export const PASSK_ONE_TRIAL_MESSAGE = 'With one trial, both numbers are the chance itself.';
export const PASSK_WIDE_MESSAGE = 'This task looks possible by pass@k and undependable by pass^k.';
export const PASSK_CLOSE_MESSAGE = 'At this chance the two promises nearly agree.';
export const passKGapMessage = (p: number, k: number) =>
  `The two numbers already differ by ${gapText(p, k)}.`;
export const PASSK_REFINEMENT =
  'Refinement: a 0.9 task passes all ten trials about a third of the time.';
export function passKSummary(state: PassKState): Summary {
  if (!state.computed) return { tone: 'neutral', message: PASSK_START_MESSAGE };
  if (state.k === 1) return { tone: 'neutral', message: PASSK_ONE_TRIAL_MESSAGE };
  const gap = gapPoints(state.chance, state.k);
  if (gap >= 40) return { tone: 'warning', message: PASSK_WIDE_MESSAGE };
  if (gap < 10) return { tone: 'success', message: PASSK_CLOSE_MESSAGE };
  return { tone: 'neutral', message: passKGapMessage(state.chance, state.k) };
}

// --- agenteval-trace: the first wrong step ---------------------------------------------------

export const TRACES = ['a7-1', 'a2-2', 'a1-1', 'speakers-all'] as const;
export type Trace = (typeof TRACES)[number];
export const traceLabels: Record<Trace, string> = {
  'a7-1': 'a7 trial 1: Is the room booked?',
  'a2-2': 'a2 trial 2: Who owns the dates task, and is it done?',
  'a1-1': 'a1 trial 1: What is blocking the launch?',
  'speakers-all': 'every piece sent: How long may each speaker talk?',
};
export const toTrace = (value: string): Trace => TRACES.find((id) => id === value) ?? 'a7-1';
export interface Step {
  what: string;
  /** Why the step was wrong, or null when it was right. */
  wrong: string | null;
}
export interface TraceRun {
  steps: readonly Step[];
  kind: string;
  owner: string;
  /** The message once the read has stopped or finished. */
  message: string;
}
const right = (what: string): Step => ({ what, wrong: null });
const wrong = (what: string, why: string): Step => ({ what, wrong: why });
export const NO_WRONG_STEP = 'none, the trial passed';
export const NOBODY = 'nobody, nothing to fix';
export const traceRuns: Record<Trace, TraceRun> = {
  'a7-1': {
    steps: [
      wrong(
        'answer with no tool: "I don\'t have access to room booking information."',
        'the task list was never read',
      ),
    ],
    kind: 'no lookup',
    owner: 'the system message',
    message: 'Step 1 answered without a lookup, so the task list was never read.',
  },
  'a2-2': {
    steps: [
      wrong(
        'answer with no tool: "I need more information. … Which project?"',
        'nothing was listed',
      ),
    ],
    kind: 'no lookup',
    owner: 'the system message',
    message: 'Step 1 asked a question instead of listing the tasks.',
  },
  'a1-1': {
    steps: [
      right('list_projects({})'),
      right('list_tasks({"project_id": 1})'),
      right('answer naming the projector, the headcount and the dates'),
    ],
    kind: NO_WRONG_STEP,
    owner: NOBODY,
    message: 'Every step was right, and the trial passed.',
  },
  'speakers-all': {
    steps: [
      wrong(
        'all 26 pieces sent in one prompt of 1,404 tokens, 12.2 with its planted sentence among them',
        'no search chose the pieces',
      ),
      wrong('answer: "The launch is cancelled."', 'a consequence of step 1'),
    ],
    kind: 'wrong source',
    owner: 'retrieval, which sent everything',
    message: 'Step 1 sent every piece, so the planted sentence reached the answer.',
  },
};
export const firstWrongStep = (run: TraceRun) => {
  const index = run.steps.findIndex((step) => step.wrong !== null);
  return index === -1 ? null : index + 1;
};
export const firstWrongStepText = (run: TraceRun) => {
  const step = firstWrongStep(run);
  return step === null ? 'none' : `step ${step}`;
};
export const stepVerdict = (step: Step) => (step.wrong === null ? 'right' : `wrong: ${step.wrong}`);
export interface TraceState {
  trace: Trace;
  /** How many steps have been read from the top. */
  read: number;
}
export const initialTrace = (): TraceState => ({ trace: 'a7-1', read: 0 });
export type TraceAction = { type: 'trace'; value: Trace } | { type: 'read' } | { type: 'reset' };
/** The read stops at the first wrong step or at the end. */
export const readStopped = (state: TraceState) => {
  const run = traceRuns[state.trace];
  const last = run.steps[state.read - 1];
  return state.read >= run.steps.length || (last !== undefined && last.wrong !== null);
};
/** The steps shown: one per press, then the whole trace once the first wrong step is reached. */
export const revealedSteps = (state: TraceState): readonly Step[] => {
  const run = traceRuns[state.trace];
  const last = run.steps[state.read - 1];
  return last !== undefined && last.wrong !== null ? run.steps : run.steps.slice(0, state.read);
};
export function traceTransition(state: TraceState, action: TraceAction): TraceState {
  switch (action.type) {
    case 'reset':
      return initialTrace();
    case 'trace':
      return { trace: action.value, read: 0 };
    case 'read':
      return readStopped(state) ? state : { ...state, read: state.read + 1 };
  }
}
export const TRACE_START_MESSAGE = 'Choose a trial, then press Read from the top.';
export const READ_FROM_TOP = 'Read from the top';
export const READ_NEXT = 'Read the next step';
export const readLabel = (state: TraceState) => (state.read === 0 ? READ_FROM_TOP : READ_NEXT);
export const stepRightMessage = (step: number) => `Step ${step} is right, so read on.`;
export const TRACE_REFINEMENT =
  "Refinement: fixing the answer's words cannot repair a lookup that never happened.";
export function traceSummary(state: TraceState): Summary {
  if (state.read === 0) return { tone: 'neutral', message: TRACE_START_MESSAGE };
  const run = traceRuns[state.trace];
  if (!readStopped(state)) return { tone: 'neutral', message: stepRightMessage(state.read) };
  return { tone: firstWrongStep(run) === null ? 'success' : 'warning', message: run.message };
}

// --- agenteval-table: one table for failures and cost ---------------------------------------

export const RUNS = ['unit2'] as const;
export type RunId = (typeof RUNS)[number];
export const toRun = (value: string): RunId => RUNS.find((id) => id === value) ?? 'unit2';
export const ORDERS = ['upstream', 'count'] as const;
export type Order = (typeof ORDERS)[number];
export const orderLabels: Record<Order, string> = {
  upstream: 'upstream order',
  count: 'count',
};
export const toOrder = (value: string): Order => ORDERS.find((id) => id === value) ?? 'upstream';
export interface FailureRow {
  kind: string;
  trials: number;
  tasks: string;
}
export interface RunTotals {
  trials: number;
  passed: number;
  tasks: number;
  k: number;
  passAtK: number;
  passPowK: number;
  promptTokens: number;
  tokensPerTrial: number;
  /** At the example rate of Lesson 3.2, shown only when the cost toggle is on. */
  costPerTask: string;
}
export interface Run {
  label: string;
  /** Rows in upstream order, one per kind. */
  rows: readonly FailureRow[];
  totals: RunTotals;
  refinement: string;
}
const row = (kind: string, trials: number, tasks = ''): FailureRow => ({ kind, trials, tasks });
export const runs: Record<RunId, Run> = {
  unit2: {
    label: 'The twenty live-task questions, graded on the data first',
    rows: [
      row('no lookup', 11, 'a7 (5), a13 (5), a2 (1)'),
      row('wrong source', 0),
      row('wrong arguments', 0),
      row('stopped', 0),
      row('wrong answer', 0),
    ],
    totals: {
      trials: 100,
      passed: 89,
      tasks: 20,
      k: 5,
      passAtK: 20,
      passPowK: 17,
      promptTokens: 206_971,
      tokensPerTrial: 2_070,
      costPerTask: '0.036 (example rate)',
    },
    refinement: 'Refinement: eleven failures with one kind is one fix.',
  },
};
export const orderedRows = (run: Run, order: Order): readonly FailureRow[] =>
  order === 'count' ? [...run.rows].sort((a, b) => b.trials - a.trials) : run.rows;
export const failedTrials = (run: Run) => run.totals.trials - run.totals.passed;
export const kindsWithFailures = (run: Run) => run.rows.filter((entry) => entry.trials > 0).length;
export const tallestKind = (run: Run) =>
  run.rows.reduce((best, entry) => (entry.trials > best.trials ? entry : best)).kind;
export const passPowText = (run: Run) => `${run.totals.passPowK} of ${run.totals.tasks}`;
export const passAtText = (run: Run) => `${run.totals.passAtK} of ${run.totals.tasks}`;
export const runTotalsText = (run: Run) =>
  [
    `${run.totals.trials} trials, ${run.totals.passed} passed`,
    `pass@${run.totals.k} ${passAtText(run)}, pass^${run.totals.k} ${passPowText(run)}`,
    `${run.totals.promptTokens.toLocaleString('en-US')} prompt tokens, about ${run.totals.tokensPerTrial.toLocaleString('en-US')} per trial`,
  ].join('\n');
export interface TableState {
  run: RunId;
  order: Order;
  cost: boolean;
  built: boolean;
}
export const initialTable = (): TableState => ({
  run: 'unit2',
  order: 'upstream',
  cost: false,
  built: false,
});
export type TableAction =
  | { type: 'run'; value: RunId }
  | { type: 'order'; value: Order }
  | { type: 'toggle-cost' }
  | { type: 'build' }
  | { type: 'reset' };
export function tableTransition(state: TableState, action: TableAction): TableState {
  switch (action.type) {
    case 'reset':
      return initialTable();
    case 'run':
      return { ...state, run: action.value, built: false };
    case 'order':
      return { ...state, order: action.value, built: false };
    case 'toggle-cost':
      return { ...state, cost: !state.cost };
    case 'build':
      return { ...state, built: true };
  }
}
export const TABLE_START_MESSAGE = 'Set the order, then press Build the table.';
export const tableMessage = (run: Run) => {
  const kinds = kindsWithFailures(run);
  if (kinds === 0) return 'No trial failed, so the table has no tall row.';
  if (kinds === 1) return `The ${tallestKind(run)} row is tallest, and every other row is empty.`;
  const others = kinds - 1;
  return `The ${tallestKind(run)} row is tallest, and ${others} other ${others === 1 ? 'row has' : 'rows have'} failures.`;
};
export function tableSummary(state: TableState): Summary {
  if (!state.built) return { tone: 'neutral', message: TABLE_START_MESSAGE };
  const run = runs[state.run];
  return { tone: kindsWithFailures(run) <= 1 ? 'success' : 'warning', message: tableMessage(run) };
}
